import React, { useEffect } from 'react';
import propTypes from 'prop-types';
import _shuffle from 'lodash/shuffle';  
import _slice from 'lodash/slice';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { TextContent } from '_ui-core-js/React/components/TextContent.jsx';
import { BackgroundMedia } from '_ui-core-js/React/components/BackgroundMedia.jsx';
import { Button } from '_ui-core-js/React/components/Button.jsx';
import { Picture } from '_ui-core-js/React/components/Picture.jsx';
import { ErrorMessage } from '_ui-core-js/React/components/ErrorMessage.jsx';
import { clearUrlQueryParam } from '_ui-core-js/utilities/clearUrlQueryParam.js';
import { MEDIA_TYPES } from '_ui-core-js/constants/config.js';
import { VIEWS } from './AiExperience.jsx';
import { fireGTMTrackingEvent } from '_ui-core-js/utilities//fireGTMTrackingEvent.js';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { LBX_GTM_TRACKING_VALUES } from '../../constants/analytics.js';

export function LoginScreen(props) {

  const { block, data, state } = props;
  const { title, subtitle, copy, media, ctas } = data;
  const { enableInstagramFlow } = state.settings;

  useEffect(() => {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.workflowEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.workflowStep,
      label: CORE_GTM_TRACKING_VALUES.label.loginScreen
    }, {
      workflow: {
        name: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience,
        step: CORE_GTM_TRACKING_VALUES.label.loginScreen,
        conversion: 0
      }
    });
  }, []);

  function clearPreviousSelections() {
    state.setSelectedColorPaletteImages([]);
    state.setColorPaletteHexValues([]);
    state.setColorPaletteHexValuesSelectedIndexes([]);
    state.setColorPalettePayload([]);
    state.setSelectedUserChoicesImages([]);
    state.setSelectedCarColorCode(undefined);
    state.setArtworkUrl(undefined);
  };

  function proceedToLoggedOutColorPaletteSelection() {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.ctaEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.clickCta,
      label: CORE_GTM_TRACKING_VALUES.label.continueWithoutLogin,
    });

    clearUrlQueryParam('code'); // remove instagram code if exists

    clearPreviousSelections();

    const gridImages = _slice(_shuffle(state.curatedImageFeed), 0, 9);
    state.setCuratedColorPaletteGridImages(gridImages);
    state.setLoggedIn(false);
    state.setActiveView(VIEWS.colorPaletteSelection.name);
  };

  function proceedToInstagramInColorPaletteSelection() {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.ctaEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.clickCta,
      label: CORE_GTM_TRACKING_VALUES.label.loginInstagram,
    });

    if (!state.instagramCode) {
       state.getInstagramAuthenticationCode(state.instagramAppId);
    } else {
      if (!state.loggedIn) {
        clearPreviousSelections();
      }
      state.setLoggedIn(true);
      state.setActiveView(VIEWS.instagramColorPaletteSelection.name);
    }
  };

  useEffect(()=> {
    if(!enableInstagramFlow) {
      proceedToLoggedOutColorPaletteSelection();
    }
  }, [enableInstagramFlow])

  return (
    <div className={`${block}__login-screen ${block}__layout--horizontal container-xxxl`}>
      <div className='row'>
        <ErrorMessage 
        message={state.error} 
        displayCondition={!!state.error}
        cssAnimationDelay={1} 
        />
        <div className={`${block}__layout--block-one col-12 col-md-5 col-lg-4`}>
          <TextContent 
          heading={title}
          subheading={subtitle}
          body={copy}
          modifier='centered-mobile'
          cssAnimationTrigger={true}
          />
          <div className={`${block}__cta-container`}>
           { enableInstagramFlow &&
            <Button 
            label={ctas.instagram.label} 
            id={ctas.instagram.id}
            type='secondary' 
            icon='instagram-color'
            onClickCallback={proceedToInstagramInColorPaletteSelection}
            cssAnimation={true} 
            cssAnimationName='vertical-top-bottom-clip'
            cssAnimationTrigger={true}
            cssAnimationDuration={0.5}
            cssAnimationDelay={1} 
            /> }
            <Button 
            label={ctas.noLogin.label} 
            id={ctas.noLogin.id} 
            type='secondary' 
            onClickCallback={proceedToLoggedOutColorPaletteSelection}
            cssAnimation={true} 
            cssAnimationName='vertical-top-bottom-clip'
            cssAnimationTrigger={true}
            cssAnimationDuration={0.5}
            cssAnimationDelay={1.5}
            />
          </div>
        </div>
        <div className={`${block}__layout--block-two col-12 col-md-6 offset-md-1 offset-lg-2`}>
          <div className='c-media__breakout c-media__breakout--cover'>
            <CssAnimation
            enter={{ name: 'vertical-top-bottom-clip', duration: 1, delay: '0.25, hidden' }}
            triggerEnter={true}
            >
              <div className={`${block}__login-media-container`}>
                {
                  media.type === MEDIA_TYPES.video ?
                    <BackgroundMedia 
                    mediaType={MEDIA_TYPES.video} 
                    mediaUrl={media.url} 
                    /> 
                  :
                    <Picture 
                    className='c-media__image c-media__image--fit'
                    base={media.url}
                    />
                }
              </div>
            </CssAnimation>
          </div>
        </div>
      </div>
    </div>
  )
};

LoginScreen.propTypes = {
  block: propTypes.string.isRequired,
  state: propTypes.object.isRequired,
  data: propTypes.object.isRequired
};
